import {
  ScheduleIndexResponse,
  SummarizeMinimizedResponse,
} from '@/dashboard/services/dashboardApi';
import { isValidDbDateString } from '@/common/utils/date.utils';
import { ValueOf } from '@/common/common.types';

export type SelectedYearType = number | null;
type ReducedPeriodsShape = { readable: string; numeric: string; monthId: number };

export const DASHBOARD_REFRESH_TIMER = 1000 * 60 * 10;

export function getShortDateFromDBDate(date: string, monthFormat: 'short' | 'long' = 'short') {
  const dateInstance = isValidDbDateString(date);

  if (dateInstance instanceof Date) {
    const short = getShortNameFromDate(dateInstance, monthFormat);
    return short;
  }

  return null;
}

function getShortNameFromDate(date: Date, monthFormat: 'short' | 'long' = 'short') {
  const short = date.toLocaleString('en-US', { month: monthFormat, year: 'numeric' });
  return monthFormat === 'short' ? short.split(' ').join('. ') : short;
}

export function getMonthNamesFromCountsResponse(counts: SummarizeMinimizedResponse) {
  const copy = { ...counts };

  for (const index in copy) {
    if (!copy[index]) {
      delete copy[index];
    }
  }

  const shortNames = Object.keys(copy)
    .map((period, index) => {
      const instance = isValidDbDateString(period);

      if (instance === false) {
        throw new Error(`Invalid date format ${period}`);
      }

      return {
        readable: getShortDateFromDBDate(period, 'long'),
        numeric: period,
        monthId: index,
        instance,
      };
    })
    .filter((count) => count.readable)
    .sort((left, right) => Number(right.instance) - Number(left.instance));

  if (shortNames.some((item) => item.readable === null)) {
    throw new Error('Invalid date format');
  }

  return shortNames as ReducedPeriodsShape[];
}

export function isValidCountsResponse(data: SummarizeMinimizedResponse | undefined | null) {
  if (data) {
    return true;
  }

  return false;
}

export function processNumericSummarizeValues(item: number, truncate = true) {
  const number = Number(item);
  if (isNaN(number)) {
    return 0;
  }

  const trunc = truncate ? Math.trunc(number) : number;

  if (isNaN(trunc)) {
    return 0;
  }

  return trunc;
}

export function deduplicateSchedulesResponse(schedules: ScheduleIndexResponse) {
  const yearTracker: number[] = [];

  const filtered = schedules.filter((schedule) => {
    const startYear = schedule.startYear;
    if (!yearTracker.includes(startYear)) {
      yearTracker.push(startYear);
      return true;
    }
    return false;
  });

  return filtered;
}

export function adjustUnsubmittedForBatchErrorTrue(counts: ValueOf<SummarizeMinimizedResponse>) {
  const batchError = counts.batchError;
  const batchIds = counts.batchIds;

  if (batchError === true && batchIds.length > 0) {
    counts.latestBatchStatus = 'U';
  }
}

export function categorizeErrorCountsFromSummarize(counts: ValueOf<SummarizeMinimizedResponse>) {
  const {
    validationErrorCounts: { contribution, preSubmitCritical, preSubmitError, preSubmitWarning },
  } = counts;

  return {
    preSubmitErrors: preSubmitError,
    postSubmitErrors: contribution,
    criticalErrors: preSubmitCritical,
    warnings: preSubmitWarning,
  };
}
